import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  PatientProgramsDeltaResponseModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientTargetsDeltaResponseModel,
} from '../api/models';
import { SupervisorApiService } from '../api/services';
import { environment } from '../../../environments/environment';

export interface TargetsSyncResult {
  targets: PatientTargetModel[];
  nextCursor: string | null;
  usedDelta: boolean;
  changedRowsCount: number;
  serverElapsedMs: number | null;
  payloadBytes: number;
  estimatedBytesSaved: number;
}

export interface ProgramsSyncResult {
  programs: PatientProgramModel[];
  nextCursor: string | null;
  usedDelta: boolean;
  changedRowsCount: number;
  serverElapsedMs: number | null;
  payloadBytes: number;
  estimatedBytesSaved: number;
}

@Injectable({
  providedIn: 'root',
})
export class PatientDataFacadeService {
  private static readonly MAX_CACHE_ENTRIES_PER_COLLECTION = 30;
  private static readonly ACADEMY_MODE_STORAGE_KEY = 'sparkAcademyMode';

  private readonly targetsCache = new Map<
    string,
    { items: PatientTargetModel[]; cursor: string | null }
  >();
  private readonly programsCache = new Map<
    string,
    { items: PatientProgramModel[]; cursor: string | null }
  >();

  constructor(private supervisorApiService: SupervisorApiService) {}

  clearPatient(patientId: number): void {
    const keyPrefix = `${patientId}|`;
    this.clearCacheByPrefix(this.targetsCache, keyPrefix);
    this.clearCacheByPrefix(this.programsCache, keyPrefix);
    this.logInfo('[SparkDelta] facade cache cleared for patient', { patientId });
  }

  clearAll(): void {
    this.targetsCache.clear();
    this.programsCache.clear();
    this.logInfo('[SparkDelta] facade cache cleared for all patients');
  }

  getTargets(
    patientId: number,
    options?: { onlyActive?: boolean; forceRefresh?: boolean }
  ): Observable<PatientTargetModel[]> {
    const onlyActive = options?.onlyActive ?? false;
    const isAcademyMode = this.isAcademyModeEnabled();
    const cacheKey = this.buildCacheKey(patientId, onlyActive, isAcademyMode);
    const cached = this.targetsCache.get(cacheKey);
    const currentTargets = options?.forceRefresh ? [] : cached?.items ?? [];
    const previousCursor = options?.forceRefresh ? null : cached?.cursor ?? null;

    this.logInfo('[SparkDelta] facade getTargets called', {
      patientId,
      onlyActive,
      isAcademyMode,
      forceRefresh: !!options?.forceRefresh,
      cacheHit: !!cached,
      cachedRows: cached?.items?.length ?? 0,
      hasCursor: !!cached?.cursor,
    });

    return this.syncTargets(patientId, onlyActive, currentTargets, previousCursor).pipe(
      map((result) => {
        this.setCacheEntry(this.targetsCache, cacheKey, {
          items: result.targets,
          cursor: result.nextCursor,
        });
        this.logInfo('[SparkDelta] facade getTargets resolved', {
          patientId,
          onlyActive,
          isAcademyMode,
          mode: result.usedDelta ? 'delta' : 'full-snapshot',
          rows: result.targets.length,
          changedRowsCount: result.changedRowsCount,
          payloadBytes: result.payloadBytes,
          estimatedBytesSaved: result.estimatedBytesSaved,
          nextCursor: result.nextCursor,
        });
        return result.targets;
      })
    );
  }

  getPrograms(
    patientId: number,
    options?: { onlyActive?: boolean; forceRefresh?: boolean }
  ): Observable<PatientProgramModel[]> {
    const onlyActive = options?.onlyActive ?? false;
    const isAcademyMode = this.isAcademyModeEnabled();
    const cacheKey = this.buildCacheKey(patientId, onlyActive, isAcademyMode);
    const cached = this.programsCache.get(cacheKey);
    const currentPrograms = options?.forceRefresh ? [] : cached?.items ?? [];
    const previousCursor = options?.forceRefresh ? null : cached?.cursor ?? null;

    this.logInfo('[SparkDelta] facade getPrograms called', {
      patientId,
      onlyActive,
      isAcademyMode,
      forceRefresh: !!options?.forceRefresh,
      cacheHit: !!cached,
      cachedRows: cached?.items?.length ?? 0,
      hasCursor: !!cached?.cursor,
    });

    return this.syncPrograms(
      patientId,
      onlyActive,
      currentPrograms,
      previousCursor
    ).pipe(
      map((result) => {
        this.setCacheEntry(this.programsCache, cacheKey, {
          items: result.programs,
          cursor: result.nextCursor,
        });
        this.logInfo('[SparkDelta] facade getPrograms resolved', {
          patientId,
          onlyActive,
          isAcademyMode,
          mode: result.usedDelta ? 'delta' : 'full-snapshot',
          rows: result.programs.length,
          changedRowsCount: result.changedRowsCount,
          payloadBytes: result.payloadBytes,
          estimatedBytesSaved: result.estimatedBytesSaved,
          nextCursor: result.nextCursor,
        });
        return result.programs;
      })
    );
  }

  syncTargets(
    patientId: number,
    onlyActive: boolean,
    currentTargets: PatientTargetModel[],
    previousCursor: string | null
  ): Observable<TargetsSyncResult> {
    const startedAtMs = Date.now();
    const isAcademyMode = this.isAcademyModeEnabled();
    this.logInfo('[SparkDelta] mode', {
      entity: 'targets',
      mode: isAcademyMode ? 'academy' : 'standard',
      patientId,
      onlyActive,
    });

    // Academy mode uses regular full calls every time:
    // simpler behavior and lower risk of mixed-mode cursor issues.
    if (isAcademyMode) {
      return this.supervisorApiService
        .GetPatientTargets({
          patientID: patientId,
          onlyActive,
        })
        .pipe(
          map((targets) => {
            const payloadBytes = this.estimatePayloadBytes(targets);
            const elapsedMs = Date.now() - startedAtMs;
            const fullResult: TargetsSyncResult = {
              targets: targets ?? [],
              nextCursor: null,
              usedDelta: false,
              changedRowsCount: (targets ?? []).length,
              serverElapsedMs: null,
              payloadBytes,
              estimatedBytesSaved: 0,
            };

            this.logInfo(
              '[SparkDelta] academy mode targets using full snapshot',
              {
                patientId,
                onlyActive,
                rows: fullResult.changedRowsCount,
                payloadBytes: fullResult.payloadBytes,
                elapsedMs,
              }
            );

            return fullResult;
          })
        );
    }

    const normalizedTargets = currentTargets;
    const normalizedCursor = previousCursor;

    return this.fetchPatientTargetsDelta(patientId, onlyActive, normalizedCursor).pipe(
      map((deltaResponse) => {
        const usedFullSnapshot = !!deltaResponse.UsedFullSnapshot || !normalizedCursor;
        const upserts = deltaResponse.Upserts ?? [];
        const deletes = deltaResponse.Deletes ?? [];
        const mergedTargets = usedFullSnapshot
          ? upserts
          : this.applyTargetsDelta(normalizedTargets, upserts, deletes);

        const payloadBytes = this.estimatePayloadBytes(deltaResponse);
        const mergedSnapshotBytes = this.estimatePayloadBytes(mergedTargets);
        const estimatedBytesSaved = Math.max(0, mergedSnapshotBytes - payloadBytes);
        const elapsedMs = Date.now() - startedAtMs;

        const result: TargetsSyncResult = {
          targets: mergedTargets,
          nextCursor: deltaResponse.NextCursor ?? normalizedCursor ?? null,
          usedDelta: !usedFullSnapshot,
          changedRowsCount:
            deltaResponse.ChangedRowsCount ?? upserts.length + deletes.length,
          serverElapsedMs: deltaResponse.ServerElapsedMs ?? null,
          payloadBytes,
          estimatedBytesSaved,
        };

        this.logInfo('[SparkDelta] targets sync complete', {
          patientId,
          mode: result.usedDelta ? 'delta' : 'full-snapshot',
          changedRowsCount: result.changedRowsCount,
          upserts: upserts.length,
          deletes: deletes.length,
          payloadBytes: result.payloadBytes,
          estimatedBytesSaved: result.estimatedBytesSaved,
          serverElapsedMs: result.serverElapsedMs,
          elapsedMs,
          nextCursor: result.nextCursor,
        });

        return result;
      }),
      catchError((error) => {
        this.logWarn(
          '[SparkDelta] targets delta failed, falling back to full snapshot',
          {
            patientId,
            onlyActive,
            message: error?.message ?? error,
          }
        );

        return this.supervisorApiService
          .GetPatientTargets({
            patientID: patientId,
            onlyActive,
          })
          .pipe(
            map((targets) => {
              const payloadBytes = this.estimatePayloadBytes(targets);
              const elapsedMs = Date.now() - startedAtMs;
              const fullResult: TargetsSyncResult = {
                targets: targets ?? [],
                nextCursor: null,
                usedDelta: false,
                changedRowsCount: (targets ?? []).length,
                serverElapsedMs: null,
                payloadBytes,
                estimatedBytesSaved: 0,
              };

              this.logInfo('[SparkDelta] targets fallback full snapshot complete', {
                patientId,
                rows: fullResult.changedRowsCount,
                payloadBytes: fullResult.payloadBytes,
                elapsedMs,
              });

              return fullResult;
            })
          );
      })
    );
  }

  syncPrograms(
    patientId: number,
    onlyActive: boolean,
    currentPrograms: PatientProgramModel[],
    previousCursor: string | null
  ): Observable<ProgramsSyncResult> {
    const startedAtMs = Date.now();
    const isAcademyMode = this.isAcademyModeEnabled();
    this.logInfo('[SparkDelta] mode', {
      entity: 'programs',
      mode: isAcademyMode ? 'academy' : 'standard',
      patientId,
      onlyActive,
    });

    // Academy mode uses regular full calls every time:
    // simpler behavior and lower risk of mixed-mode cursor issues.
    if (isAcademyMode) {
      return this.supervisorApiService
        .GetPatientPrograms({
          patientID: patientId,
          onlyActive,
        })
        .pipe(
          map((programs) => {
            const payloadBytes = this.estimatePayloadBytes(programs);
            const elapsedMs = Date.now() - startedAtMs;
            const fullResult: ProgramsSyncResult = {
              programs: programs ?? [],
              nextCursor: null,
              usedDelta: false,
              changedRowsCount: (programs ?? []).length,
              serverElapsedMs: null,
              payloadBytes,
              estimatedBytesSaved: 0,
            };

            this.logInfo(
              '[SparkDelta] academy mode programs using full snapshot',
              {
                patientId,
                onlyActive,
                rows: fullResult.changedRowsCount,
                payloadBytes: fullResult.payloadBytes,
                elapsedMs,
              }
            );

            return fullResult;
          })
        );
    }

    const normalizedPrograms = currentPrograms;
    const normalizedCursor = previousCursor;

    return this.fetchPatientProgramsDelta(patientId, onlyActive, normalizedCursor).pipe(
      map((deltaResponse) => {
        const usedFullSnapshot = !!deltaResponse.UsedFullSnapshot || !normalizedCursor;
        const upserts = deltaResponse.Upserts ?? [];
        const deletes = deltaResponse.Deletes ?? [];
        const mergedPrograms = usedFullSnapshot
          ? upserts
          : this.applyProgramsDelta(normalizedPrograms, upserts, deletes);

        const payloadBytes = this.estimatePayloadBytes(deltaResponse);
        const mergedSnapshotBytes = this.estimatePayloadBytes(mergedPrograms);
        const estimatedBytesSaved = Math.max(0, mergedSnapshotBytes - payloadBytes);
        const elapsedMs = Date.now() - startedAtMs;

        const result: ProgramsSyncResult = {
          programs: mergedPrograms,
          nextCursor: deltaResponse.NextCursor ?? normalizedCursor ?? null,
          usedDelta: !usedFullSnapshot,
          changedRowsCount:
            deltaResponse.ChangedRowsCount ?? upserts.length + deletes.length,
          serverElapsedMs: deltaResponse.ServerElapsedMs ?? null,
          payloadBytes,
          estimatedBytesSaved,
        };

        this.logInfo('[SparkDelta] programs sync complete', {
          patientId,
          mode: result.usedDelta ? 'delta' : 'full-snapshot',
          changedRowsCount: result.changedRowsCount,
          upserts: upserts.length,
          deletes: deletes.length,
          payloadBytes: result.payloadBytes,
          estimatedBytesSaved: result.estimatedBytesSaved,
          serverElapsedMs: result.serverElapsedMs,
          elapsedMs,
          nextCursor: result.nextCursor,
        });

        return result;
      }),
      catchError((error) => {
        this.logWarn(
          '[SparkDelta] programs delta failed, falling back to full snapshot',
          {
            patientId,
            onlyActive,
            message: error?.message ?? error,
          }
        );

        return this.supervisorApiService
          .GetPatientPrograms({
            patientID: patientId,
            onlyActive,
          })
          .pipe(
            map((programs) => {
              const payloadBytes = this.estimatePayloadBytes(programs);
              const elapsedMs = Date.now() - startedAtMs;
              const fullResult: ProgramsSyncResult = {
                programs: programs ?? [],
                nextCursor: null,
                usedDelta: false,
                changedRowsCount: (programs ?? []).length,
                serverElapsedMs: null,
                payloadBytes,
                estimatedBytesSaved: 0,
              };

              this.logInfo('[SparkDelta] programs fallback full snapshot complete', {
                patientId,
                rows: fullResult.changedRowsCount,
                payloadBytes: fullResult.payloadBytes,
                elapsedMs,
              });

              return fullResult;
            })
          );
      })
    );
  }

  private fetchPatientTargetsDelta(
    patientId: number,
    onlyActive: boolean,
    sinceCursor: string | null
  ): Observable<PatientTargetsDeltaResponseModel> {
    return this.supervisorApiService.GetPatientTargetsDelta({
      patientID: patientId,
      onlyActive,
      sinceCursor: sinceCursor ?? undefined,
    });
  }

  private fetchPatientProgramsDelta(
    patientId: number,
    onlyActive: boolean,
    sinceCursor: string | null
  ): Observable<PatientProgramsDeltaResponseModel> {
    return this.supervisorApiService.GetPatientProgramsDelta({
      patientID: patientId,
      onlyActive,
      sinceCursor: sinceCursor ?? undefined,
    });
  }

  private applyTargetsDelta(
    currentTargets: PatientTargetModel[],
    upserts: PatientTargetModel[],
    deletes: number[]
  ): PatientTargetModel[] {
    const mergedById = new Map<number, PatientTargetModel>();

    for (const target of currentTargets ?? []) {
      if (!target.ID) {
        continue;
      }
      mergedById.set(target.ID, target);
    }

    for (const target of upserts ?? []) {
      if (!target.ID) {
        continue;
      }
      mergedById.set(target.ID, target);
    }

    for (const targetId of deletes ?? []) {
      mergedById.delete(targetId);
    }

    return Array.from(mergedById.values());
  }

  private applyProgramsDelta(
    currentPrograms: PatientProgramModel[],
    upserts: PatientProgramModel[],
    deletes: number[]
  ): PatientProgramModel[] {
    const mergedById = new Map<number, PatientProgramModel>();

    for (const program of currentPrograms ?? []) {
      if (!program.ID) {
        continue;
      }
      mergedById.set(program.ID, program);
    }

    for (const program of upserts ?? []) {
      if (!program.ID) {
        continue;
      }
      mergedById.set(program.ID, program);
    }

    for (const programId of deletes ?? []) {
      mergedById.delete(programId);
    }

    return Array.from(mergedById.values());
  }

  private estimatePayloadBytes(payload: unknown): number {
    if (payload == null) {
      return 0;
    }

    try {
      return new Blob([JSON.stringify(payload)]).size;
    } catch {
      return -1;
    }
  }

  private buildCacheKey(
    patientId: number,
    onlyActive: boolean,
    isAcademyMode: boolean
  ): string {
    return `${patientId}|${onlyActive ? 'active' : 'all'}|${
      isAcademyMode ? 'academy' : 'standard'
    }`;
  }

  private isAcademyModeEnabled(): boolean {
    return (
      sessionStorage.getItem(PatientDataFacadeService.ACADEMY_MODE_STORAGE_KEY) ===
      '1'
    );
  }

  private clearCacheByPrefix<T>(cache: Map<string, T>, keyPrefix: string): void {
    const keysToDelete: string[] = [];
    for (const key of cache.keys()) {
      if (key.startsWith(keyPrefix)) {
        keysToDelete.push(key);
      }
    }
    for (const key of keysToDelete) {
      cache.delete(key);
    }
  }

  private setCacheEntry<T>(cache: Map<string, T>, key: string, value: T): void {
    if (cache.has(key)) {
      cache.delete(key);
    }
    cache.set(key, value);

    while (cache.size > PatientDataFacadeService.MAX_CACHE_ENTRIES_PER_COLLECTION) {
      const oldestKey = cache.keys().next().value as string | undefined;
      if (!oldestKey) {
        break;
      }
      cache.delete(oldestKey);
      this.logInfo('[SparkDelta] facade cache evicted oldest entry', {
        key: oldestKey,
        maxEntries: PatientDataFacadeService.MAX_CACHE_ENTRIES_PER_COLLECTION,
      });
    }
  }

  private isLoggingEnabled(): boolean {
    if (!environment.production) {
      return true;
    }
    return localStorage.getItem('spark-api-perf-log') === '1';
  }

  private logInfo(message: string, data?: unknown): void {
    if (!this.isLoggingEnabled()) {
      return;
    }
    console.info(message, data ?? {});
  }

  private logWarn(message: string, data?: unknown): void {
    if (!this.isLoggingEnabled()) {
      return;
    }
    console.warn(message, data ?? {});
  }
}
